import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import View                      from '@widesk-ui/components/View';
import Spin                      from '@widesk-ui/components/Spin';
import { InboxOutlined }         from '@ant-design/icons';
import useDesignTokens           from '@/hooks/useDesignTokens';
import planImportStore           from '@/TokamapEditor/planImport/planImportStore';
import { PLAN_FILE_ACCEPT }      from '@/TokamapEditor/planImport/planAnalysisApi';
import { RegionIllustration }    from '@/TokamapEditor/planImport/PlanStepIllustrations';
import { ReviewIllustration }    from '@/TokamapEditor/planImport/PlanStepIllustrations';
import { PlacementIllustration } from '@/TokamapEditor/planImport/PlanStepIllustrations';

const FORMATS = [
	{ label: 'DXF, SVG, PDF vectoriel', detail: 'formes et numéros lus' },
	{ label: 'PDF scanné, PNG, JPG, WEBP', detail: 'formes seules, numéros à saisir' },
];

const STAGES = [
	{ title: 'Délimitez la zone utile', text: 'Entourez la partie du plan à analyser, sans le cartouche ni la légende.', Illustration: RegionIllustration },
	{ title: 'Vérifiez ce qui est reconnu', text: 'Les emplacements et leurs numéros sont repérés ; tranchez les quelques cas douteux.', Illustration: ReviewIllustration },
	{ title: 'Posez le plan sur la carte', text: 'Calez-le à son emplacement : les formes sont créées là, et les POI existants mis à jour.', Illustration: PlacementIllustration },
];

const PlanFileStep = observer(() => {
	const tokens = useDesignTokens();
	const input = React.useRef<HTMLInputElement>(null);
	const [dragging, setDragging] = React.useState(false);

	const pick = (files: FileList | null) => {
		const file = files?.[0];
		if (file) void planImportStore.start(file);
	};

	return (
		<View flex p="lg" gap={18} style={{ alignItems: 'center', justifyContent: 'safe center', overflowY: 'auto' }}>
			<View gap={6} style={{ width: 'min(960px, 100%)' }}>
				<View size={18} style={{ fontWeight: 600 }}>Transformez votre plan en carte</View>
				<View size={14} color={tokens.text.secondary} style={{ lineHeight: 1.55 }}>
					Tokamap repère sur votre plan les stands, salles ou emplacements et leurs numéros, puis
					les crée en formes sur la carte : plus besoin de les dessiner un à un.
				</View>
			</View>

			<View row gap={20} wrap style={{ width: 'min(960px, 100%)', alignItems: 'stretch' }}>
				<video
					src={`${import.meta.env.BASE_URL}tool-previews/planImport.webm`}
					width={384}
					height={216}
					autoPlay
					muted
					loop
					playsInline
					style={{ display: 'block', maxWidth: '100%', borderRadius: 12, background: tokens.surface.subtle, border: `1px solid ${tokens.border.subtle}` }}
				/>

				<View flex style={{ minWidth: 300 }}>
					<div
						className={dragging || planImportStore.busy ? 'plan-drop-zone' : 'plan-drop-zone plan-drop-zone--inviting'}
						style={{
							'--plan-drop-halo': tokens.color.primary,
							display: 'flex',
							flex: 1,
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 10,
							cursor: planImportStore.busy ? 'progress' : 'pointer',
							padding: 24,
							borderRadius: 12,
							border: `2px dashed ${dragging ? tokens.color.primary : `color-mix(in srgb, ${tokens.color.primary} 55%, transparent)`}`,
							background: `color-mix(in srgb, ${tokens.color.primary} ${dragging ? 10 : 4}%, ${tokens.surface.card})`,
						} as React.CSSProperties}
						onClick={() => !planImportStore.busy && input.current?.click()}
						onDragOver={event => {
							event.preventDefault();
							setDragging(true);
						}}
						onDragLeave={() => setDragging(false)}
						onDrop={event => {
							event.preventDefault();
							setDragging(false);
							if (!planImportStore.busy) pick(event.dataTransfer.files);
						}}
					>
						{planImportStore.busy
							? <Spin size="large" />
							: <InboxOutlined className="plan-drop-zone__icon" style={{ fontSize: 36, color: tokens.color.primary }} />}

						<View size={15} style={{ fontWeight: 600, textAlign: 'center' }}>
							{planImportStore.busy ? 'Lecture du plan…' : 'Déposez votre plan ici, ou cliquez pour le choisir'}
						</View>

						<View gap={4} center>
							{FORMATS.map(format => (
								<View key={format.label} size={12} color={tokens.text.secondary}>
									<span><b>{format.label}</b> — {format.detail}</span>
								</View>
							))}
						</View>

						<input
							ref={input}
							type="file"
							accept={PLAN_FILE_ACCEPT}
							style={{ display: 'none' }}
							onChange={event => {
								pick(event.target.files);
								event.target.value = '';
							}}
						/>
					</div>
				</View>
			</View>

			<View gap={8} style={{ width: 'min(960px, 100%)' }}>
				<View size={13} color={tokens.text.muted} style={{ fontWeight: 600 }}>Ensuite, en trois étapes</View>

				<View row gap={12} wrap>
					{STAGES.map(({ title, text, Illustration }, index) => (
						<View key={title} flex row gap={12} style={{ minWidth: 280, padding: 10, borderRadius: 12, alignItems: 'center', border: `1px solid ${tokens.border.subtle}`, background: tokens.surface.card }}>
							<View style={{ width: 108, height: 60, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: tokens.surface.subtle }}>
								<Illustration />
							</View>

							<View flex gap={2}>
								<View row gap={6} style={{ alignItems: 'baseline' }}>
									<View color={tokens.color.primary} style={{ fontWeight: 700 }}>{index + 1}</View>
									<View style={{ fontWeight: 600 }}>{title}</View>
								</View>

								<View size={12} color={tokens.text.secondary} style={{ lineHeight: 1.45 }}>{text}</View>
							</View>
						</View>
					))}
				</View>
			</View>
		</View>
	);
});

export default PlanFileStep;
